import moment, { type Moment } from 'moment';
import React from 'react';

export const formats = Object.freeze({
  default: 'DD/MM/YYYY',
  short: 'Do MMMM YYYY',
  shortNoYear: 'Do MMMM',
  shortDay: 'ddd Do MMMM YYYY',
  long: 'dddd Do MMMM YYYY',
  birthday: 'MMMM YYYY',
  monthAndYear: 'MMMM YYYY',
  day: 'dddd',
  longNoYear: 'dddd Do MMMM',
  week: 'Do MMMM YYYY',
  dynamic: 'dynamic',
  diaryHeader: 'ddd Do'
});

export type DateFormat = keyof typeof formats;

/** Formats a date value using a named format. Useful outside of JSX. */
export const formatDate = (
  value: moment.MomentInput,
  format: DateFormat = 'default',
  skipUtc = false
): string => {
  const date = skipUtc ? moment(value) : moment.utc(value);
  if (!date.isValid() || date.year() <= 1) return '';
  if (format === 'dynamic') {
    return date.calendar({
      sameDay: '[Today]',
      nextDay: '[Tomorrow]',
      nextWeek: 'ddd Do MMM',
      lastDay: '[Yesterday]',
      lastWeek: '[Last] dddd',
      sameElse: (now) => `ddd Do MMM${moment(now).isSame(new Date(), 'year') ? '' : ' YYYY'}`
    });
  }
  if (format === 'week') {
    const firstDayOfWeek = date.clone().weekday(1);
    return `Week of ${firstDayOfWeek.format(formats['week'])}`;
  }
  return date.format(formats[format] || formats['default']);
};

export interface IProps {
  fallback: React.ReactNode;
  format: DateFormat;
  skipUtc: boolean;
  value: moment.MomentInput;
  className?: string;
}

const getFormat = (format: DateFormat, date: Moment) => {
  if (date && format === 'week') {
    const firstDayOfWeek = date.clone().weekday(1);
    return `Week of ${firstDayOfWeek.format(formats['week'])}`;
  }

  return date.format(formats[format] || formats['default']);
};

class DateLabel extends React.Component<IProps> {
  static defaultProps: Partial<IProps> = {
    fallback: '',
    format: 'default',
    className: ''
  };

  render() {
    let { value, skipUtc, className = '', format } = this.props;
    let date = skipUtc ? moment(value) : moment.utc(value);
    const dateString =
      format === 'dynamic'
        ? date.calendar({
            sameDay: '[Today]',
            nextDay: '[Tomorrow]',
            nextWeek: 'ddd Do MMM',
            lastDay: '[Yesterday]',
            lastWeek: '[Last] dddd',
            sameElse: (now) => `ddd Do MMM${moment(now).isSame(new Date(), 'year') ? '' : ' YYYY'}`
          })
        : getFormat(format, date);
    if (value && date.isValid() && date.year() > 1) {
      return <span className={className}>{dateString}</span>;
    } else {
      return <span className={className}>{this.props.fallback}</span>;
    }
  }
}

export default DateLabel;
